import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Dropdown, DropdownButton, FormControl, InputGroup, Spinner } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import debounce from 'lodash/debounce'

import { hasFeature, onUpgradePlan } from 'utils/general'
import { showUpgradeCardModal } from 'actions'
import { showCustomDomainModal, showMessageModal } from 'Manage/actions'
import { F_CUSTOM_DOMAIN } from 'constants/plans/constants'
import { PLANS } from 'constants/plans'
import { REGEX_DOMAIN } from '../../../constants'
import styles from './DomainSelector.module.css'

const DomainSelector = ({
  userProfile,
  customDomains = [],
  tlds,
  onDomainChange,
  disableProFeatures = false,
  placeholder = 'my-site-name (optional)',
  disabled = false,
  className = '',
  onHide
}) => {
  const dispatch = useDispatch()
  const { productId, siteLimit } = userProfile

  const [subdomain, setSubdomain] = useState('')
  const [domainSuffix, setDomainSuffix] = useState(tlds[0])
  const [isCustomDomain, setIsCustomDomain] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [domainError, setDomainError] = useState(null)
  const [isValid, setIsValid] = useState(true)
  const debouncedValidationRef = useRef()

  const checkIsCustomDomain = useCallback(
    (suffix) => {
      return !tlds.includes(suffix)
    },
    [tlds]
  )

  const notifyParent = useCallback(
    (sub, suffix, isCustom, valid) => {
      onDomainChange({
        subdomain: sub || null,
        domainSuffix: suffix,
        isCustomDomain: isCustom,
        isValid: valid
      })
    },
    [onDomainChange]
  )

  const validateSubdomainFormat = (subdomain) => {
    if (!subdomain) return { isValid: true, error: null }

    if (!REGEX_DOMAIN.test(subdomain?.toLowerCase())) {
      return {
        isValid: false,
        error: 'Invalid link-name: Only letters, numbers, and hyphens/dashes are allowed.'
      }
    }

    return { isValid: true, error: null }
  }

  const validateDomain = async (subdomainValue, suffixValue = domainSuffix) => {
    if (!subdomainValue?.trim()) {
      setDomainError(null)
      setIsValid(true)
      notifyParent(subdomainValue, suffixValue, isCustomDomain, true)
      return
    }

    const formatValidation = validateSubdomainFormat(subdomainValue.trim())

    if (!formatValidation.isValid) {
      setDomainError(formatValidation.error)
      setIsValid(false)
      notifyParent(subdomainValue, suffixValue, isCustomDomain, false)
      return
    }

    setDomainError(null)
    setIsValid(true)
    notifyParent(subdomainValue, suffixValue, isCustomDomain, true)
  }

  useEffect(() => {
    debouncedValidationRef.current = debounce((subdomain, suffix) => {
      validateDomain(subdomain, suffix)
    }, 500)

    return () => {
      if (debouncedValidationRef.current) {
        debouncedValidationRef.current.cancel()
      }
    }
  }, [])

  const handleSubdomainChange = (e) => {
    const value = e.target.value
    setSubdomain(value)
    if (value.trim()) {
      debouncedValidationRef.current(value, domainSuffix)
    } else {
      setDomainError(null)
      setIsValid(true)
      notifyParent(value, domainSuffix, isCustomDomain, true)
    }
  }

  const handleSuffixSelect = (eventKey, event) => {
    const selectedSuffix = event.target.text
    const isCustom = checkIsCustomDomain(selectedSuffix)

    setDomainSuffix(selectedSuffix)
    setIsCustomDomain(isCustom)

    if (subdomain.trim()) {
      validateDomain(subdomain, selectedSuffix)
    } else {
      notifyParent(subdomain, selectedSuffix, isCustom, true)
    }
  }

  const handleAddCustomDomain = () => {
    onHide()
    if (disableProFeatures || !hasFeature(productId, F_CUSTOM_DOMAIN)) {
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to use custom domains',
          desc: 'Custom domains are available on Pro plans and above. Upload more on a different plan.',
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
        })
      )
    } else if (siteLimit === customDomains.length) {
      dispatch(
        showMessageModal({
          title: 'Limit reached',
          message: 'Custom domain limit reached for your current plan'
        })
      )
    } else {
      dispatch(showCustomDomainModal(true))
    }
  }

  useEffect(() => {
    notifyParent(subdomain, domainSuffix, isCustomDomain, true)
  }, [])

  const getErrorMessage = () => {
    if (domainError) {
      return domainError
    }
    return null
  }

  const errorMessage = getErrorMessage()
  const showSuccess = !isChecking && isValid && subdomain.trim()
  const showError = !isChecking && errorMessage
  const showAutoGenerate = !subdomain.trim() && !isChecking

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.inputGroup}>
        <InputGroup>
          <FormControl
            value={subdomain}
            onChange={handleSubdomainChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`${styles.subdomainInput} ${showError ? styles.inputError : ''}`}
            aria-label="Link name (optional)"
          />

          <DropdownButton
            as={InputGroup.Append}
            variant="outline-secondary"
            title={domainSuffix}
            onSelect={handleSuffixSelect}
            disabled={disabled}
            className={`${styles.suffixDropdown} ${showError ? styles.inputError : ''} ${showSuccess ? styles.inputSuccess : ''}`}
          >
            {tlds.map((tld) => (
              <Dropdown.Item key={tld} className={styles.dropdownItem}>
                {tld}
              </Dropdown.Item>
            ))}

            {customDomains.length > 0 && (
              <>
                {customDomains.map((domain) => {
                  const suffix = `.${domain.domain}`
                  return (
                    <Dropdown.Item key={suffix} className={styles.dropdownItem} active={domainSuffix === suffix}>
                      {suffix}
                    </Dropdown.Item>
                  )
                })}
              </>
            )}

            {!isCustomDomain && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleAddCustomDomain} className={styles.addCustomDomain}>
                  <img src="/assets/icons/plus-circle.svg" alt="add" width="16" height="16" />
                  Add custom domain
                </Dropdown.Item>
              </>
            )}
          </DropdownButton>
        </InputGroup>
      </div>

      <div className={styles.statusContainer}>
        {isChecking && (
          <div className={`${styles.statusItem} ${styles.checking}`}>
            <Spinner animation="border" size="sm" className={styles.spinner} />
            <span>Checking availability...</span>
          </div>
        )}

        {showError && (
          <div className={`${styles.statusItem} ${styles.error}`}>
            <img src="/assets/icons/warning-yellow.svg" alt="error" width="12" height="12" />
            <span>{errorMessage}</span>
          </div>
        )}

        {showSuccess && (
          <div className={`${styles.statusItem} ${styles.success}`}>
            <img src="/assets/icons/check.svg" alt="success" width="12" height="12" />
            <span>Available!</span>
          </div>
        )}

        {showAutoGenerate && (
          <div className={`${styles.statusItem} ${styles.autoGenerate}`}>
            <img src="/assets/icons/info-circle.svg" alt="info" width="12" height="12" />
            <span>Link name will be auto-generated, if none is provided</span>
          </div>
        )}
      </div>

      {subdomain.trim() && domainSuffix && isValid && (
        <div className={styles.previewUrl}>
          Your site will be available at:{' '}
          <strong>
            {subdomain.trim()}
            {domainSuffix}
          </strong>
        </div>
      )}

      {!subdomain.trim() && (
        <div className={styles.previewUrl}>
          Your site will be available at: <strong>&lt;auto-generated&gt;{domainSuffix}</strong>
        </div>
      )}
    </div>
  )
}

export default DomainSelector
